import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Media from 'local_modules/Media'

import Box from 'components/layout/Box/Box'
import Text from 'components/typography/Text/Text'
import Input from 'components/ui/form/Input/Input'
import Select from 'components/ui/form/Select/Select'
import Editor from 'components/ui/form/Editor/Editor'
import TextArea from 'components/ui/form/TextArea/TextArea'
import FileInput from 'components/ui/form/FileInput/FileInput'
import Selectable from 'components/ui/form/Selectable/Selectable'
import CustomSelect from 'components/ui/form/CustomSelect/CustomSelect'
import CheckBoxView from 'components/ui/form/CheckBoxView/CheckBoxView'
import { Grid, Cell } from 'components/layout/grid'
import WeekCheckBoxes from 'components/ui/form/WeekCheckBoxes/WeekCheckBoxes'

import s from './EditableFields.scss'

import editImage from './images/edit.svg'


const EditableFields = ({ children, className, rowClassName, fieldsRow, onRemove, isRemovable, onEdit, removeSize, editSize, columns, getFields }) => (
  <Media mobile>
    {
      (isMobile) => (
        <Grid className={cx(s.container, className, rowClassName)} columns={columns || 8} gutter="md">
          {
            fieldsRow.map((field, index) => {
              const {
                fieldType, valueLink, label, placeholder, fieldName, options, size, mobileSize, inputType,
                calendarOptions, mask, mobileHidden, boxProps = {}, inputProps = {}, component, onChange, checkIsVisible,
              } = field

              const isInput         = fieldType === 'input'
              const isFile          = fieldType === 'file'
              const isSelect        = fieldType === 'select'
              const isCustomSelect  = fieldType === 'customSelect'
              const isText          = fieldType === 'text'
              const isTextArea      = fieldType === 'textArea'
              const isEditor        = fieldType === 'editor'
              const isLabel         = fieldType === 'label'
              const isBox           = fieldType === 'box'
              const isCheckBox      = fieldType === 'checkbox'
              const isWeek          = fieldType === 'weekCheckBoxes'
              const isSelectable    = fieldType === 'selectable'
              const cellSize        = (isMobile ? mobileSize : size) || 8
              const isVisible = !checkIsVisible || checkIsVisible(fieldsRow)

              if (!isVisible || isInput && !valueLink) {
                return null
              }

              if (isSelectable) {
                return (
                  <Cell key={index} size={cellSize}>
                    <Selectable
                      placeholder={placeholder}
                      options={options}
                      valueLink={valueLink}
                    />
                  </Cell>
                )
              }

              if (isWeek) {
                const dates = [
                  {
                    day: 'MONDAY',
                    weekDay: 'ПН',
                  },
                  {
                    day: 'TUESDAY',
                    weekDay: 'ВТ',
                  },
                  {
                    day: 'WEDNESDAY',
                    weekDay: 'СР',
                  },
                  {
                    day: 'THURSDAY',
                    weekDay: 'ЧТ',
                  },
                  {
                    day: 'FRIDAY',
                    weekDay: 'ПТ',
                  },
                  {
                    day: 'SATURDAY',
                    weekDay: 'СБ',
                  },
                  {
                    day: 'SUNDAY',
                    weekDay: 'ВС',
                  },
                ]

                return (
                  <Cell key={index} size={cellSize}>
                    <WeekCheckBoxes
                      dates={dates}
                      selectedDates={valueLink.value}
                      isWeekDaysOnly
                      onChange={(weekDay) => {
                        const isSelected = valueLink.value.includes(weekDay)
                        const newValue = isSelected
                          ? valueLink.value.filter((day) => day !== weekDay)
                          : valueLink.value.concat(weekDay)

                        valueLink.set(newValue)
                      }}
                    />
                    {
                      Boolean(valueLink.error) && (
                        <Text
                          message={valueLink.error}
                          color="red"
                          size="xs"
                        />
                      )
                    }
                  </Cell>
                )
              }

              if (isBox) {
                return (
                  <Cell key={index} size={cellSize}>
                    <Box {...boxProps}>
                      {typeof component === 'function' ? component({ getFields }) : component}
                    </Box>
                  </Cell>
                )
              }

              if (isInput) {
                return (
                  <Cell key={index} size={cellSize} style={inputType === 'hidden' ? { display: 'none' } : null}>
                    <Input
                      className={s.input}
                      valueLink={valueLink}
                      placeholder={placeholder}
                      label={label}
                      type={inputType}
                      calendarOptions={calendarOptions}
                      mask={mask}
                      {...inputProps}
                    />
                  </Cell>
                )
              }

              if (isCheckBox) {
                return (
                  <Cell key={index} size={cellSize} >
                    <Box {...boxProps}>
                      <div
                        className={s.checkBox}
                        onClick={() => {
                          valueLink.set(!valueLink.value)

                          if (typeof onChange === 'function') {
                            onChange(!valueLink.value)
                          }
                        }}
                      >
                        <CheckBoxView
                          isChecked={valueLink.value}
                        />
                        <Box className={s.checkBoxLabel} pl="xs">
                          {label}
                          {
                            Boolean(valueLink.error) && (
                              <div style={{ color: 'red' }}>
                                {valueLink.error}
                              </div>
                            )
                          }
                        </Box>
                      </div>
                    </Box>
                  </Cell>
                )
              }

              if (isFile) {
                return (
                  <Cell key={index} size={cellSize}>
                    <FileInput
                      className={s.input}
                      label={label}
                      placeholder={placeholder}
                      valueLink={valueLink}
                      withImage
                      {...inputProps}
                    />
                  </Cell>
                )
              }

              if (isTextArea) {
                return (
                  <Cell key={index} size={cellSize}>
                    <TextArea
                      className={s.input}
                      valueLink={valueLink}
                      placeholder={placeholder}
                      label={label}
                    />
                  </Cell>
                )
              }

              if (isEditor) {
                return (
                  <Cell key={index} size={cellSize}>
                    <Editor
                      className={s.input}
                      valueLink={valueLink}
                      placeholder={placeholder}
                      label={label}
                    />
                  </Cell>
                )
              }

              if (isSelect) {
                return (
                  <Cell key={index} size={cellSize}>
                    <Select
                      key={index}
                      className={s.input}
                      label={label}
                      valueLink={valueLink}
                      placeholder={placeholder}
                      options={typeof options === 'function' ? options(fieldsRow) : options}
                      onChange={onChange}
                    />
                  </Cell>
                )
              }

              if (isCustomSelect) {
                return (
                  <Cell key={index} size={cellSize}>
                    <CustomSelect
                      key={index}
                      className={s.input}
                      label={label}
                      valueLink={valueLink}
                      // placeholder={placeholder}
                      options={typeof options === 'function' ? options(fieldsRow) : options}
                      onChange={onChange}
                    />
                  </Cell>
                )
              }

              if (isText) {
                return (!mobileHidden || !isMobile) && (
                  <Cell key={index} className={s.input} size={cellSize}>
                    {placeholder}
                  </Cell>
                )
              }

              if (isLabel) {
                return (
                  <Cell
                    key={index}
                    className={s.label}
                    size={cellSize}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: placeholder }}
                    />
                  </Cell>
                )
              }

              // if (isCheckbox) {
              //   return (
              //     <CheckBox
              //       key={`${rowIndex}${index}`}
              //       className={s.input}
              //       valueLink={valueLink}
              //       label={placeholder}
              //       onClick={(value) => updateFieldValues({ [fieldName]: value })}
              //     />
              //   )
              // }
            })
          }
          {
            Boolean(onEdit) && (
              <Cell size={editSize || 8} center>
                <div className={s.crossWrapper}>
                  <img src={editImage} className={s.icon} onClick={onEdit}/>
                </div>
              </Cell>
            )
          }
          {
            isRemovable && (
              <Cell size={removeSize || 8} center>
                <div className={s.crossWrapper}>
                  <div
                    className={s.cross}
                    onClick={onRemove}
                  />
                </div>
              </Cell>
            )
          }
        </Grid>
      )
    }
  </Media>
)

EditableFields.propTypes = {
  className: PropTypes.string,
}


export default EditableFields
